import {inject, observer} from "mobx-react";
import React, {useCallback, useEffect, useState, useMemo, useRef} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPencilAlt, faWindowClose, faCog} from "@fortawesome/free-solid-svg-icons";
import Checkbox from "../components/checkbox";
import PaginationControls from "../components/pagination-controls";
import Switch from "react-switch";
import Modal from "../components/modal";
import ExpenseNavigationBar from "../components/expense_configuration-navigation-bar";
import {fetch} from "../utils/api-wrapper";
import MoneyInput from "../components/money-input";
import CurrencySelect from "../components/currency-select";
import Utils from "../utils/utils";
import ImputationConfigModal from "../components/imputation-config-modal";
import { buildMultiSelectPickerParts } from "../components/multi-select-chip-picker";
import { imputationDocumentId } from "./review-expense/utils/contract-expense-json";
import {
    imputationConfigRowDisplayName,
    imputationRowHasParentId,
    normalizeRankIdsFromApi,
    resolveImputationEditTarget,
    IMPUTATION_FETCH_PAGE_SIZE,
} from "../utils/expenses-imputations-config-helpers";
import {loadXpensCategories, getXpensCategoryLabel} from "../utils/xpens-categories";

/**
 * Validate a travel type form object against the T002 API schema.
 * The enum lists below must stay in sync with their counterparts in
 * xpens-api/src/services/expenses_client_rates_service.py:
 * _VALID_CLIENT_SCOPES, _VALID_BILLABLE_SCOPES, _VALID_TRAVEL_MODES,
 * _VALID_CURRENCIES, _VALID_DISTANCE_UNITS.
 */
export const validateTravelTypeForm = (form, readLabel) => {
    const errors = [];
    if (!String(form.name ?? "").trim()) {
        errors.push(readLabel("travel_type_validation_name_required", "Travel type name is required."));
    }
    if (!["client", "internal"].includes(form.client_scope)) {
        errors.push(readLabel("travel_type_validation_client_scope_required", "Client scope must be selected."));
    }
    if (!["billable", "non_billable"].includes(form.billable_scope)) {
        errors.push(readLabel("travel_type_validation_billable_scope_required", "Billable scope must be selected."));
    }
    // XPEN-2871: defense-in-depth backstop — the UI already prevents selecting
    // this combination (Billable option is hidden once Client Scope = Internal),
    // but reject it here too in case the form state is ever reached another way.
    if (form.client_scope === "internal" && form.billable_scope === "billable") {
        errors.push(readLabel("travel_type_validation_internal_billable_not_allowed", "Internal travel types cannot be billable."));
    }
    if (!["office_travel", "other_travel", "open_amount"].includes(form.travel_mode)) {
        errors.push(readLabel("travel_type_validation_travel_mode_required", "Travel mode must be selected."));
    }
    if (!String(form.imputation_id ?? "").trim()) {
        errors.push(readLabel("travel_type_validation_imputation_required", "A displacement imputation must be selected."));
    }
    // open_amount has no distance to measure, so it carries neither a rate nor a
    // distance unit — the API rejects either field if sent for this mode.
    if (form.travel_mode !== "open_amount") {
        const rate = Number(form.rate);
        if (!Number.isFinite(rate) || rate <= 0) {
            errors.push(readLabel("travel_type_validation_rate_required", "Rate must be a positive number."));
        }
    }
    if (!/^[A-Z]{3}$/.test(form.currency || "")) {
        errors.push(readLabel("travel_type_validation_currency_required", "Currency must be selected."));
    }
    if (form.travel_mode !== "open_amount" && !["km", "miles"].includes(form.distance_unit)) {
        errors.push(readLabel("travel_type_validation_distance_unit_required", "Distance unit must be selected."));
    }
    return errors;
};

/** Generate a stable unique id for a new travel type (no external dependency). */
const newTravelTypeId = () =>
    `tt-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 11)}`;

/**
 * Strip rate/distance_unit for an open_amount travel type before it is sent to
 * the API, which rejects either field for this mode. The form itself never
 * clears these two fields on a mode switch — that way, switching to
 * open_amount and back restores exactly what the administrator had typed,
 * instead of losing it. Only the outgoing payload omits them.
 */
const buildTravelTypePayload = (form) => {
    if (form.travel_mode !== "open_amount") return form;
    const { rate, distance_unit, ...rest } = form;
    return rest;
};

const EMPTY_TRAVEL_TYPE_FORM = {
    id: "",
    name: "",
    active: true,
    client_scope: "client",
    billable_scope: "non_billable",
    travel_mode: "office_travel",
    imputation_id: "",
    rate: "",
    currency: "CAD",
    distance_unit: "km",
    department_ids: [],
    rank_ids: [],
};

const REQUIRED_MARK = <span className="text-danger" aria-hidden="true"> *</span>;

const DISPLACEMENT_SORTING = encodeURIComponent(JSON.stringify([{ id: "category", desc: false }]));
const DISPLACEMENT_VISIBLE_COLUMNS_STORAGE_KEY = "xpens.displacement-imputations.visible-columns.v2";
const DEFAULT_DISPLACEMENT_VISIBLE_COLUMNS = {
    origin: true,
    ranks: true,
    subcategory: true,
    glNatural: true,
    costType: true,
    tax: true,
    approvers: true,
};

const TRAVEL_TYPE_VISIBLE_COLUMNS_STORAGE_KEY = "xpens.mileage-travel-types.visible-columns.v1";
const DEFAULT_TRAVEL_TYPE_VISIBLE_COLUMNS = {
    clientScope: true,
    billableScope: true,
    travelMode: true,
    imputation: true,
    rate: true,
};

const ExpensesRates = ({data}) => {
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [rates, setRates] = useState({});

    // — Displacement imputations section —
    const [displacementImputations, setDisplacementImputations] = useState([]);
    const [showDisplacementModal, setShowDisplacementModal] = useState(false);
    const [displacementModalInitial, setDisplacementModalInitial] = useState(null);
    const [approvableUsers, setApprovableUsers] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [rankOptions, setRankOptions] = useState([]);
    const [displacementVisibleColumns, setDisplacementVisibleColumns] = useState(DEFAULT_DISPLACEMENT_VISIBLE_COLUMNS);
    const [isDisplacementSettingsOpen, setIsDisplacementSettingsOpen] = useState(false);
    const [displacementDeleteSelection, setDisplacementDeleteSelection] = useState([]);
    const [showDisplacementDeleteModal, setShowDisplacementDeleteModal] = useState(false);
    const displacementSettingsPanelRef = useRef(null);

    const hasAderantIntegration = Utils.hasAderantIntegration(data);
    const isLegalEngine = Utils.isEngineType(data, "legal");
    // XPEN-3493: one predicate for the whole page, matching the pattern
    // imputation-config-modal.js already uses. Inlining it at every control is
    // how the Super Administrator came to be missing from some gates and not
    // others, and it leaves each gate individually revertible without any test
    // noticing.
    //
    // Do not read this as "the server enforces the same thing". It does for the
    // displacement section: POST/PUT/DELETE /imputations/, plus import and
    // add-defaults, all carry access_levels=["SuperAdmin", "Admin", "Manager"].
    // It does not for the travel types on this page — PUT /expense_rates/<client_id>
    // is a bare @verify_access() with no role list, and takes client_id from the
    // URL rather than the caller's token. So for that half of the screen this
    // check is the only one there is, which is a frontend gate standing in for a
    // server one. Tracked separately; do not lean on it.
    const canEdit = Utils.userHasRole(data, ["SuperAdmin", "Admin", "Manager"]);
    const showDisplacementRanksColumn = hasAderantIntegration && displacementVisibleColumns.ranks;

    // parentIds derived directly from the already-loaded displacement imputations —
    // avoids a duplicate paginated fetch on mount.
    const displacementParentIds = useMemo(() => {
        const pIds = new Set();
        displacementImputations.forEach(row => {
            if (row.parent_id) pIds.add(String(row.parent_id));
        });
        return pIds;
    }, [displacementImputations]);

    // Approvers + departments loaded eagerly — departments needed for travel type form.
    useEffect(() => {
        fetch("/client/users/approvers", "GET", {}, (response) => {
            const users = response?.data ?? [];
            setApprovableUsers(users.map(u => ({
                value: u.id_user,
                label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.id_user,
            })));
        });
    }, []);

    useEffect(() => {
        const deptSearchData = `?filters=${encodeURIComponent(JSON.stringify([{ id: "id_client", value: data.id_client }]))}&sorting=${encodeURIComponent(JSON.stringify([]))}&start=0&page_size=100`;
        fetch(`/departments/${deptSearchData}`, "GET", {}, (response) => {
            const depts = response?.data?.departments ?? [];
            setDepartmentOptions(
                depts.map((d) => {
                    const id = d._id?.$oid ?? d._id ?? d.id;
                    if (id == null || id === "") return null;
                    return { value: String(id), label: d.name || "" };
                }).filter(Boolean)
            );
        });
    }, [data.id_client]);

    // Ranks are Aderant-specific — only needed when the modal opens.
    const displacementDropdownsLoadedRef = useRef(false);
    const loadDisplacementModalDropdowns = useCallback(() => {
        if (displacementDropdownsLoadedRef.current) return;
        displacementDropdownsLoadedRef.current = true;
        if (hasAderantIntegration) {
            fetch("/client/options/?field_name=Rank", "GET", {}, (response) => {
                setRankOptions(Utils.mapRankOptionsFromApi(response?.data));
            });
        }
    }, [hasAderantIntegration]);

    // — Mileage travel types section —
    const [showTravelTypeModal, setShowTravelTypeModal] = useState(false);
    const [editingTravelType, setEditingTravelType] = useState(null);
    const [travelTypeForm, setTravelTypeForm] = useState({});
    const [travelTypeFormErrors, setTravelTypeFormErrors] = useState([]);
    // Tracks the Level 1 root selection independently from imputation_id.
    // Needed because imputation_id is cleared when a root with children is chosen
    // (to force child selection), but Level 1 must still show the selected root.
    const [travelTypePickerRootId, setTravelTypePickerRootId] = useState("");
    const [travelTypeVisibleColumns, setTravelTypeVisibleColumns] = useState(DEFAULT_TRAVEL_TYPE_VISIBLE_COLUMNS);
    const [isTravelTypeSettingsOpen, setIsTravelTypeSettingsOpen] = useState(false);
    const [travelTypeDeleteSelection, setTravelTypeDeleteSelection] = useState([]);
    const [showTravelTypeDeleteModal, setShowTravelTypeDeleteModal] = useState(false);
    const travelTypeSettingsPanelRef = useRef(null);

    // — Pagination state —
    const [displacementPage, setDisplacementPage] = useState(1);
    const [displacementPageSize, setDisplacementPageSize] = useState(10);
    const [travelTypePage, setTravelTypePage] = useState(1);
    const [travelTypePageSize, setTravelTypePageSize] = useState(10);

    useEffect(() => {
        refreshRates();
    }, []);

    useEffect(() => {
        loadXpensCategories(data);
    }, [data]);

    const refreshDisplacementImputations = useCallback(() => {
        const filters = [{ id: "imputation_catalog", value: "displacement" }];
        const all = [];
        const fetchPage = (start) => {
            const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters))}&sorting=${DISPLACEMENT_SORTING}&start=${start}&page_size=${IMPUTATION_FETCH_PAGE_SIZE}`;
            fetch(`/imputations/${searchData}`, "GET", {}, (response) => {
                const rows = response?.data?.imputations ?? [];
                const totalRowCount = response?.data?.meta?.totalRowCount ?? 0;
                all.push(...rows);
                if (rows.length > 0 && all.length < totalRowCount) {
                    fetchPage(start + rows.length);
                } else {
                    setDisplacementImputations([...all]);
                }
            });
        };
        fetchPage(0);
    }, []);

    useEffect(() => {
        refreshDisplacementImputations();
    }, [refreshDisplacementImputations]);

    // Restore persisted column visibility
    useEffect(() => {
        try {
            const raw = localStorage.getItem(DISPLACEMENT_VISIBLE_COLUMNS_STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            setDisplacementVisibleColumns(prev => ({
                ...prev,
                ...(parsed && typeof parsed === "object" ? parsed : {}),
            }));
        } catch (_) {}
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(DISPLACEMENT_VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(displacementVisibleColumns));
        } catch (_) {}
    }, [displacementVisibleColumns]);

    // Click-outside / Escape to close settings panel
    useEffect(() => {
        if (!isDisplacementSettingsOpen) return undefined;
        const onMouseDown = (e) => {
            if (!displacementSettingsPanelRef.current) return;
            if (!displacementSettingsPanelRef.current.contains(e.target)) {
                setIsDisplacementSettingsOpen(false);
            }
        };
        const onKeyDown = (e) => {
            if (e.key === "Escape") setIsDisplacementSettingsOpen(false);
        };
        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [isDisplacementSettingsOpen]);

    // Travel type column visibility persistence
    useEffect(() => {
        try {
            const raw = localStorage.getItem(TRAVEL_TYPE_VISIBLE_COLUMNS_STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw);
            setTravelTypeVisibleColumns(prev => ({ ...prev, ...(parsed && typeof parsed === "object" ? parsed : {}) }));
        } catch (_) {}
    }, []);

    useEffect(() => {
        try {
            localStorage.setItem(TRAVEL_TYPE_VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(travelTypeVisibleColumns));
        } catch (_) {}
    }, [travelTypeVisibleColumns]);

    useEffect(() => {
        if (!isTravelTypeSettingsOpen) return undefined;
        const onMouseDown = (e) => {
            if (!travelTypeSettingsPanelRef.current) return;
            if (!travelTypeSettingsPanelRef.current.contains(e.target)) setIsTravelTypeSettingsOpen(false);
        };
        const onKeyDown = (e) => { if (e.key === "Escape") setIsTravelTypeSettingsOpen(false); };
        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [isTravelTypeSettingsOpen]);

    const readLabel = useCallback((key, fallback) => {
        const value = data.t.read(key);
        return (typeof value === "string" && value.trim() !== "") ? value : fallback;
    }, [data.t]);

    const originLabel = useCallback((origin) => {
        if (origin === "verify_default") return data.t.read("imputation_origin_verify_default");
        if (origin === "client") return data.t.read("imputation_origin_client");
        return origin ?? "";
    }, [data.t]);

    // Label maps for table display
    const approverIdToLabel = useMemo(() => {
        const m = {};
        approvableUsers.forEach(u => { m[u.value] = u.label; });
        return m;
    }, [approvableUsers]);

    const rankIdToLabel = useMemo(() => {
        const m = {};
        rankOptions.forEach(r => { m[r.value] = r.label; });
        return m;
    }, [rankOptions]);

    // XPEN-3494: the travel type Departments chips resolve their labels through
    // this, the same way the Ranks chips use rankIdToLabel above.
    const departmentIdToLabel = useMemo(() => {
        const m = {};
        departmentOptions.forEach(d => { m[d.value] = d.label; });
        return m;
    }, [departmentOptions]);

    // XPEN-3494: both Visibility fields are the same picker with different field
    // names, so wrap the factory at the call site the way the Allocations modal
    // does. One helper rather than one per field type — Mileage has a single call
    // site each, so a per-type wrapper would only ever be used once.
    // canEdit and the remove label are the same for both and stay in here.
    const renderTravelTypeVisibilityPicker = useCallback(
        ({ inputId, selectedIds, onChangeIds, options, resolveLabel, placeholder, chipTestId }) => {
            const { control, selectionList } = buildMultiSelectPickerParts({
                inputId,
                selectedIds,
                onChangeIds,
                options,
                resolveLabel,
                placeholder,
                chipTestId,
                editAllowed: canEdit,
                removeLabel: data.t.read("delete"),
            });
            return (
                <>
                    {control}
                    {selectionList}
                </>
            );
        },
        [canEdit, data.t]
    );

    const formatDisplacementApprovers = useCallback((row) => {
        const ids = row?.approver_user_ids ?? [];
        if (ids.length === 0) return "—";
        return ids.map(id => approverIdToLabel[id] || id).join(", ");
    }, [approverIdToLabel]);

    const formatDisplacementRanks = useCallback((row) => {
        const ids = normalizeRankIdsFromApi(row);
        if (ids.length === 0) return "—";
        return ids.map(id => rankIdToLabel[id] || id).join(", ");
    }, [rankIdToLabel]);

    const toggleDisplacementColumn = useCallback((key) => {
        setDisplacementVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const resetDisplacementVisibleColumns = useCallback(() => {
        setDisplacementVisibleColumns(DEFAULT_DISPLACEMENT_VISIBLE_COLUMNS);
    }, []);

    // Displacement rows displayed at root level only; children are managed inside the modal.
    // Grouped parent-child structure — same pattern as the Allocations table.
    const displacementGroups = useMemo(() => {
        const byId = {};
        const childrenByParent = {};
        displacementImputations.forEach(row => {
            const id = imputationDocumentId(row);
            if (!id) return;
            byId[id] = row;
        });
        displacementImputations.forEach(row => {
            const parentId = row.parent_id ? String(row.parent_id) : null;
            if (!parentId) return;
            if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
            childrenByParent[parentId].push(row);
        });
        const groups = [];
        const seenParents = new Set();
        displacementImputations.filter(row => {
            const parentId = row.parent_id ? String(row.parent_id) : null;
            return !parentId || !byId[parentId];
        }).forEach(parent => {
            const parentId = imputationDocumentId(parent);
            if (!parentId || seenParents.has(parentId)) return;
            seenParents.add(parentId);
            groups.push({ parent, children: childrenByParent[parentId] || [] });
        });
        return groups;
    }, [displacementImputations]);

    const displacementIndexOfFirst = (displacementPage - 1) * displacementPageSize;
    const paginatedDisplacementGroups = useMemo(
        () => displacementGroups.slice(displacementIndexOfFirst, displacementIndexOfFirst + displacementPageSize),
        [displacementGroups, displacementIndexOfFirst, displacementPageSize]
    );
    const displacementTotalPages = Math.max(1, Math.ceil(displacementGroups.length / displacementPageSize));
    const displacementLastVisible = Math.min(displacementIndexOfFirst + displacementPageSize, displacementGroups.length);
    const displacementIndexOfLast = displacementLastVisible > 0 ? displacementLastVisible - 1 : 0;

    const allTravelTypes = rates.mileage_travel_types ?? [];
    const travelTypeIndexOfFirst = (travelTypePage - 1) * travelTypePageSize;
    const paginatedTravelTypes = useMemo(
        () => allTravelTypes.slice(travelTypeIndexOfFirst, travelTypeIndexOfFirst + travelTypePageSize),
        [allTravelTypes, travelTypeIndexOfFirst, travelTypePageSize]
    );
    const travelTypeTotalPages = Math.max(1, Math.ceil(allTravelTypes.length / travelTypePageSize));
    const travelTypeLastVisible = Math.min(travelTypeIndexOfFirst + travelTypePageSize, allTravelTypes.length);
    const travelTypeIndexOfLast = travelTypeLastVisible > 0 ? travelTypeLastVisible - 1 : 0;

    // Two-level imputation picker for the travel type modal.
    // Level 1 shows displacement roots; Level 2 shows children when a root with
    // subcategories is selected. Resolved from the already-loaded displacementImputations
    // — no extra API calls needed.
    const displacementRootOptions = useMemo(
        () => displacementImputations.filter(imp => !imputationRowHasParentId(imp)),
        [displacementImputations]
    );

    const selectedDisplacementRoot = useMemo(() => {
        // Primary source: explicit Level 1 picker state (set by user interaction).
        // This stays populated even when imputation_id is cleared for child selection.
        if (travelTypePickerRootId) {
            return displacementImputations.find(
                imp => String(imputationDocumentId(imp)) === String(travelTypePickerRootId)
            ) ?? null;
        }
        // Fallback: resolve from imputation_id for edit pre-fill (picker not yet touched).
        const id = travelTypeForm.imputation_id;
        if (!id) return null;
        const asRoot = displacementImputations.find(
            imp => String(imputationDocumentId(imp)) === String(id) && !imputationRowHasParentId(imp)
        );
        if (asRoot) return asRoot;
        const asChild = displacementImputations.find(
            imp => String(imputationDocumentId(imp)) === String(id) && imputationRowHasParentId(imp)
        );
        return asChild
            ? displacementImputations.find(imp => String(imputationDocumentId(imp)) === String(asChild.parent_id)) ?? null
            : null;
    }, [travelTypePickerRootId, travelTypeForm.imputation_id, displacementImputations]);

    const displacementChildrenForPicker = useMemo(() => {
        if (!selectedDisplacementRoot) return [];
        const rootId = String(imputationDocumentId(selectedDisplacementRoot));
        return displacementImputations.filter(imp => String(imp.parent_id) === rootId);
    }, [selectedDisplacementRoot, displacementImputations]);

    const isAllDisplacementSelected = () => {
        if (!displacementGroups.length) return false;
        return displacementGroups.every(({ parent }) =>
            displacementDeleteSelection.includes(imputationDocumentId(parent))
        );
    };

    const toggleAllDisplacementSelected = () => {
        const allIds = displacementGroups.map(({ parent }) => imputationDocumentId(parent)).filter(Boolean);
        if (isAllDisplacementSelected()) {
            setDisplacementDeleteSelection(displacementDeleteSelection.filter(id => !allIds.includes(id)));
        } else {
            setDisplacementDeleteSelection([...new Set([...displacementDeleteSelection, ...allIds])]);
        }
    };

    const handleDisplacementRowClick = useCallback((imputation) => {
        const { parentRow, focusedChildId, isParentMode } = resolveImputationEditTarget(
            displacementImputations,
            imputation,
            displacementParentIds
        );
        loadDisplacementModalDropdowns();
        setDisplacementModalInitial({ parentRow, focusedChildId, isParentMode });
        setShowDisplacementModal(true);
    }, [displacementImputations, displacementParentIds, loadDisplacementModalDropdowns]);

    const deleteDisplacementImputations = () => {
        fetch("/imputations/", "DELETE", { imputation_ids: displacementDeleteSelection },
            () => {
                refreshDisplacementImputations();
                setDisplacementDeleteSelection([]);
                setShowDisplacementDeleteModal(false);
                setDisplacementPage(1);
            },
            () => {
                setErrorMessage(data.t.read("failed_to_delete"));
                setShowDisplacementDeleteModal(false);
            }
        );
    };

    // — Mileage travel types callbacks —

    const saveTravelTypes = useCallback((nextTypes, onSaveSuccess) => {
        const client_id = data.id_client;
        const formData = new FormData();
        // Spread `rates` (server-fetched) so mileage_details is preserved in the DB until XPEN-2615 removes the API fallback.
        formData.append("rates", JSON.stringify({ ...(rates ?? {}), mileage_travel_types: nextTypes }));
        fetch(`/expense_rates/${client_id}`, "PUT", formData, (response) => {
            if (response.data?.status === "success") {
                setSuccessMessage(data.t.read("update_successful"));
                refreshRates();
                onSaveSuccess?.();
            } else {
                setErrorMessage(data.t.read("failed_to_update"));
            }
        }, () => {
            setErrorMessage(data.t.read("failed_to_update"));
        });
    }, [rates, data]);

    const toggleTravelTypeActive = useCallback((tt) => {
        const currentTypes = rates.mileage_travel_types ?? [];
        const nextTypes = currentTypes.map(t => t.id === tt.id ? { ...t, active: !t.active } : t);
        saveTravelTypes(nextTypes);
    }, [rates, saveTravelTypes]);

    const openNewTravelType = () => {
        loadDisplacementModalDropdowns(); // ensures rank options are available for Aderant clients
        setTravelTypeForm({ ...EMPTY_TRAVEL_TYPE_FORM });
        setTravelTypeFormErrors([]);
        setTravelTypePickerRootId("");
        setEditingTravelType(null);
        setShowTravelTypeModal(true);
    };

    const openEditTravelType = useCallback((tt) => {
        loadDisplacementModalDropdowns(); // ensures rank options are available for Aderant clients
        // XPEN-2871: clamp legacy Internal + Billable rows (saved before this guard
        // existed) so the Billable Scope select never opens on a hidden option.
        const clampedBillableScope =
            tt.client_scope === "internal" && tt.billable_scope === "billable"
                ? "non_billable"
                : tt.billable_scope;
        // Ensure optional scoping fields default to [] for legacy types without them.
        setTravelTypeForm({ department_ids: [], rank_ids: [], ...tt, billable_scope: clampedBillableScope });
        setTravelTypeFormErrors([]);
        setTravelTypePickerRootId(""); // let selectedDisplacementRoot resolve from imputation_id
        setEditingTravelType(tt);
        setShowTravelTypeModal(true);
    }, [loadDisplacementModalDropdowns]);

    const closeTravelTypeModal = () => {
        setShowTravelTypeModal(false);
        setTravelTypeForm({});
        setTravelTypePickerRootId("");
        setEditingTravelType(null);
        setTravelTypeFormErrors([]);
    };

    const closeTravelTypeModalAfterSave = () => {
        setTravelTypePage(1);
        closeTravelTypeModal();
    };

    const handleTravelTypeSubmit = (e) => {
        e.preventDefault();
        const errors = validateTravelTypeForm(travelTypeForm, readLabel);
        if (errors.length > 0) {
            setTravelTypeFormErrors(errors);
            return;
        }
        const currentTypes = rates.mileage_travel_types ?? [];
        const payload = buildTravelTypePayload(travelTypeForm);
        if (editingTravelType) {
            const nextTypes = currentTypes.map(tt =>
                tt.id === editingTravelType.id
                    ? { ...payload }
                    : tt
            );
            saveTravelTypes(nextTypes, closeTravelTypeModalAfterSave);
        } else {
            const newType = { ...payload, id: newTravelTypeId() };
            saveTravelTypes([...currentTypes, newType], closeTravelTypeModalAfterSave);
        }
    };

    const toggleTravelTypeColumn = useCallback((key) => {
        setTravelTypeVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
    }, []);

    const resetTravelTypeVisibleColumns = useCallback(() => {
        setTravelTypeVisibleColumns(DEFAULT_TRAVEL_TYPE_VISIBLE_COLUMNS);
    }, []);

    const isAllTravelTypesSelected = () => {
        const types = rates.mileage_travel_types ?? [];
        if (!types.length) return false;
        return types.every(tt => travelTypeDeleteSelection.includes(tt.id));
    };

    const toggleAllTravelTypesSelected = () => {
        const types = rates.mileage_travel_types ?? [];
        const allIds = types.map(tt => tt.id).filter(Boolean);
        if (isAllTravelTypesSelected()) {
            setTravelTypeDeleteSelection(prev => prev.filter(id => !allIds.includes(id)));
        } else {
            setTravelTypeDeleteSelection(prev => [...new Set([...prev, ...allIds])]);
        }
    };

    const deleteSelectedTravelTypes = () => {
        const currentTypes = rates.mileage_travel_types ?? [];
        const nextTypes = currentTypes.filter(tt => !travelTypeDeleteSelection.includes(tt.id));
        saveTravelTypes(nextTypes, () => {
            setTravelTypeDeleteSelection([]);
            setShowTravelTypeDeleteModal(false);
            setTravelTypePage(1);
        });
    };

    const handleDisplacementPageChange = (p) => setDisplacementPage(p);
    const handleDisplacementRowsPerPageChange = (rows) => { setDisplacementPage(1); setDisplacementPageSize(rows); };
    const handleTravelTypePageChange = (p) => setTravelTypePage(p);
    const handleTravelTypeRowsPerPageChange = (rows) => { setTravelTypePage(1); setTravelTypePageSize(rows); };

    const categoryLabel = (category) => getXpensCategoryLabel(data, category);

    const refreshRates = () => {
        fetch("/expense_rates/", "GET", {}, (response) => {
            const fetchedRates = response.data?.rates;
            setRates(fetchedRates);
        }, () => {
            setErrorMessage(data.t.read("failed_to_fetch_rates"));
        });
    };

    return (<div className="card pl-4 pr-4 pb-3 rounded-lg">
        <ExpenseNavigationBar data={data} page={"rates"}/>
        <div>
            <div>
                <div>
                    {/* — Displacement imputations section — */}
                    <div className="pb-3 border-bottom border-secondary">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h4>{data.t.read("displacement_imputations")} :</h4>
                            <div className="d-flex align-items-center">
                                <PaginationControls
                                    page={displacementPage}
                                    totalPages={displacementTotalPages}
                                    indexOfFirstItem={displacementIndexOfFirst}
                                    indexOfLastItem={displacementIndexOfLast}
                                    handlePageChange={handleDisplacementPageChange}
                                    totalItems={displacementGroups.length}
                                    data={data}
                                    handleRowsPerPageChange={handleDisplacementRowsPerPageChange}
                                    rowsPerPage={displacementPageSize}
                                />
                                {/* Settings */}
                                <div className="position-relative" ref={displacementSettingsPanelRef}>
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm ml-2 d-flex align-items-center"
                                        onClick={() => setIsDisplacementSettingsOpen(prev => !prev)}
                                        aria-expanded={isDisplacementSettingsOpen}>
                                        <FontAwesomeIcon icon={faCog} className="mr-2" />
                                        {readLabel("settings", "Settings")}
                                    </button>
                                    {isDisplacementSettingsOpen && (
                                        <div className="border rounded p-2 bg-white shadow-sm"
                                            style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 1200, width: "280px" }}>
                                            <div className="small text-muted mb-2">
                                                {readLabel("table_config_content", "Choose which columns to display in the table.")}
                                            </div>
                                            {hasAderantIntegration && (
                                                <div className="custom-control custom-checkbox mb-1">
                                                    <input id="dcol-ranks" type="checkbox" className="custom-control-input"
                                                        checked={displacementVisibleColumns.ranks}
                                                        onChange={() => toggleDisplacementColumn("ranks")} />
                                                    <label className="custom-control-label" htmlFor="dcol-ranks">
                                                        {readLabel("imputation_ranks", "Ranks")}
                                                    </label>
                                                </div>
                                            )}
                                            <div className="custom-control custom-checkbox mb-1">
                                                <input id="dcol-subcategory" type="checkbox" className="custom-control-input"
                                                    checked={displacementVisibleColumns.subcategory}
                                                    onChange={() => toggleDisplacementColumn("subcategory")} />
                                                <label className="custom-control-label" htmlFor="dcol-subcategory">
                                                    {readLabel("field_imputation_subcategory", "Subcategory")}
                                                </label>
                                            </div>
                                            <div className="custom-control custom-checkbox mb-1">
                                                <input id="dcol-origin" type="checkbox" className="custom-control-input"
                                                    checked={displacementVisibleColumns.origin}
                                                    onChange={() => toggleDisplacementColumn("origin")} />
                                                <label className="custom-control-label" htmlFor="dcol-origin">
                                                    {readLabel("imputation_origin", "Origin")}
                                                </label>
                                            </div>
                                            <div className="custom-control custom-checkbox mb-1">
                                                <input id="dcol-glNatural" type="checkbox" className="custom-control-input"
                                                    checked={displacementVisibleColumns.glNatural}
                                                    onChange={() => toggleDisplacementColumn("glNatural")} />
                                                <label className="custom-control-label" htmlFor="dcol-glNatural">
                                                    {readLabel("gl_natural", "GL Natural")}
                                                </label>
                                            </div>
                                            {isLegalEngine && (
                                                <div className="custom-control custom-checkbox mb-1">
                                                    <input id="dcol-costType" type="checkbox" className="custom-control-input"
                                                        checked={displacementVisibleColumns.costType}
                                                        onChange={() => toggleDisplacementColumn("costType")} />
                                                    <label className="custom-control-label" htmlFor="dcol-costType">
                                                        {readLabel("cost_type", "Cost Type")}
                                                    </label>
                                                </div>
                                            )}
                                            <div className="custom-control custom-checkbox mb-1">
                                                <input id="dcol-tax" type="checkbox" className="custom-control-input"
                                                    checked={displacementVisibleColumns.tax}
                                                    onChange={() => toggleDisplacementColumn("tax")} />
                                                <label className="custom-control-label" htmlFor="dcol-tax">
                                                    {readLabel("tax", "Tax")}
                                                </label>
                                            </div>
                                            <div className="custom-control custom-checkbox mb-2">
                                                <input id="dcol-approvers" type="checkbox" className="custom-control-input"
                                                    checked={displacementVisibleColumns.approvers}
                                                    onChange={() => toggleDisplacementColumn("approvers")} />
                                                <label className="custom-control-label" htmlFor="dcol-approvers">
                                                    {readLabel("imputation_approvers", "Approvers")}
                                                </label>
                                            </div>
                                            <button type="button" className="btn btn-link btn-sm p-0" onClick={resetDisplacementVisibleColumns}>
                                                {readLabel("reset", "Reset")}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {/* Delete (N) */}
                                <button
                                    type="button"
                                    disabled={!canEdit || displacementDeleteSelection.length === 0}
                                    onClick={() => setShowDisplacementDeleteModal(true)}
                                    className="btn btn-secondary btn-sm ml-2">
                                    {data.t.read("delete")} ({displacementDeleteSelection.length})
                                </button>
                                {/* Add */}
                                {canEdit && (
                                    <button
                                        type="button"
                                        data-testid="displacement-add"
                                        className="btn btn-primary btn-sm ml-2"
                                        onClick={() => {
                                            loadDisplacementModalDropdowns();
                                            setDisplacementModalInitial(null);
                                            setShowDisplacementModal(true);
                                        }}>
                                        {data.t.read("add")}
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="card rounded-lg admin-table" style={{ maxHeight: "35vh", overflowY: "auto" }}>
                            <table className="table table-hover font-small mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: "40px" }}>
                                            <Checkbox
                                                disabled={!displacementGroups.length}
                                                isChecked={isAllDisplacementSelected()}
                                                onChange={toggleAllDisplacementSelected} />
                                        </th>
                                        <th>{data.t.read("category")}</th>
                                        {showDisplacementRanksColumn && <th>{data.t.read("imputation_ranks")}</th>}
                                        {displacementVisibleColumns.subcategory && <th>{readLabel("field_imputation_subcategory", "Subcategory")}</th>}
                                        {displacementVisibleColumns.origin && <th>{data.t.read("imputation_origin")}</th>}
                                        {displacementVisibleColumns.glNatural && <th>{data.t.read("gl_natural")}</th>}
                                        {isLegalEngine && displacementVisibleColumns.costType && <th>{data.t.read("cost_type")}</th>}
                                        {displacementVisibleColumns.tax && <th>{data.t.read("tax")}</th>}
                                        {displacementVisibleColumns.approvers && <th>{data.t.read("imputation_approvers")}</th>}
                                        <th style={{ width: "80px" }}>{readLabel("table_actions", "Actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedDisplacementGroups.flatMap(({ parent, children }) => {
                                        const parentId = imputationDocumentId(parent);
                                        const isSelected = displacementDeleteSelection.includes(parentId);
                                        const hasChildren = children.length > 0;
                                        const codingRows = hasChildren ? children : [parent];
                                        return codingRows.map((codingRow, index) => (
                                            <tr key={`${parentId}-${imputationDocumentId(codingRow)}-${index}`}
                                                onClick={() => handleDisplacementRowClick(codingRow)}
                                                style={{ cursor: "pointer" }}>
                                                {index === 0 && (
                                                    <td rowSpan={codingRows.length} onClick={e => e.stopPropagation()}>
                                                        <Checkbox
                                                            isChecked={isSelected}
                                                            onChange={() => {
                                                                if (isSelected) {
                                                                    setDisplacementDeleteSelection(displacementDeleteSelection.filter(x => x !== parentId));
                                                                } else {
                                                                    setDisplacementDeleteSelection([...displacementDeleteSelection, parentId]);
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                )}
                                                {index === 0 && (
                                                    <td rowSpan={codingRows.length}>
                                                        {imputationConfigRowDisplayName(parent, categoryLabel, data)}
                                                    </td>
                                                )}
                                                {showDisplacementRanksColumn && index === 0 && (
                                                    <td rowSpan={codingRows.length}>{formatDisplacementRanks(parent)}</td>
                                                )}
                                                {displacementVisibleColumns.subcategory && (
                                                    <td>{hasChildren ? imputationConfigRowDisplayName(codingRow, categoryLabel, data) : "—"}</td>
                                                )}
                                                {displacementVisibleColumns.origin && <td>{originLabel(codingRow.origin)}</td>}
                                                {displacementVisibleColumns.glNatural && <td>{codingRow.gl_natural || "—"}</td>}
                                                {isLegalEngine && displacementVisibleColumns.costType && <td>{codingRow.cost_type || "—"}</td>}
                                                {displacementVisibleColumns.tax && <td>{codingRow.tax_code || "—"}</td>}
                                                {displacementVisibleColumns.approvers && <td>{formatDisplacementApprovers(codingRow)}</td>}
                                                {index === 0 && (
                                                    <td rowSpan={codingRows.length}>
                                                        <FontAwesomeIcon
                                                            className="mr-2"
                                                            icon={faPencilAlt}
                                                            onClick={e => { e.stopPropagation(); handleDisplacementRowClick(parent); }}
                                                        />
                                                        <FontAwesomeIcon
                                                            icon={faWindowClose}
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                setDisplacementDeleteSelection([parentId]);
                                                                setShowDisplacementDeleteModal(true);
                                                            }}
                                                            style={{ color: "red", cursor: "pointer" }}
                                                        />
                                                    </td>
                                                )}
                                            </tr>
                                        ));
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* — Mileage travel types section — */}
                    <div className="pb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <h4>{readLabel("mileage_travel_types", "Mileage Travel Types")} :</h4>
                            <div className="d-flex align-items-center">
                                <PaginationControls
                                    page={travelTypePage}
                                    totalPages={travelTypeTotalPages}
                                    indexOfFirstItem={travelTypeIndexOfFirst}
                                    indexOfLastItem={travelTypeIndexOfLast}
                                    handlePageChange={handleTravelTypePageChange}
                                    totalItems={allTravelTypes.length}
                                    data={data}
                                    handleRowsPerPageChange={handleTravelTypeRowsPerPageChange}
                                    rowsPerPage={travelTypePageSize}
                                />
                                {/* Settings */}
                                <div className="position-relative" ref={travelTypeSettingsPanelRef}>
                                    <button type="button"
                                        className="btn btn-light btn-sm ml-2 d-flex align-items-center"
                                        onClick={() => setIsTravelTypeSettingsOpen(prev => !prev)}
                                        aria-expanded={isTravelTypeSettingsOpen}>
                                        <FontAwesomeIcon icon={faCog} className="mr-2" />
                                        {readLabel("settings", "Settings")}
                                    </button>
                                    {isTravelTypeSettingsOpen && (
                                        <div className="border rounded p-2 bg-white shadow-sm"
                                            style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 1200, width: "240px" }}>
                                            <div className="small text-muted mb-2">
                                                {readLabel("table_config_content", "Choose which columns to display in the table.")}
                                            </div>
                                            <div className="custom-control custom-checkbox mb-1">
                                                <input id="ttcol-clientScope" type="checkbox" className="custom-control-input"
                                                    checked={travelTypeVisibleColumns.clientScope}
                                                    onChange={() => toggleTravelTypeColumn("clientScope")} />
                                                <label className="custom-control-label" htmlFor="ttcol-clientScope">
                                                    {readLabel("travel_type_client_scope", "Client Scope")}
                                                </label>
                                            </div>
                                            <div className="custom-control custom-checkbox mb-1">
                                                <input id="ttcol-billableScope" type="checkbox" className="custom-control-input"
                                                    checked={travelTypeVisibleColumns.billableScope}
                                                    onChange={() => toggleTravelTypeColumn("billableScope")} />
                                                <label className="custom-control-label" htmlFor="ttcol-billableScope">
                                                    {readLabel("travel_type_billable_scope", "Billable Scope")}
                                                </label>
                                            </div>
                                            <div className="custom-control custom-checkbox mb-1">
                                                <input id="ttcol-travelMode" type="checkbox" className="custom-control-input"
                                                    checked={travelTypeVisibleColumns.travelMode}
                                                    onChange={() => toggleTravelTypeColumn("travelMode")} />
                                                <label className="custom-control-label" htmlFor="ttcol-travelMode">
                                                    {readLabel("travel_type_travel_mode", "Travel Mode")}
                                                </label>
                                            </div>
                                            <div className="custom-control custom-checkbox mb-1">
                                                <input id="ttcol-imputation" type="checkbox" className="custom-control-input"
                                                    checked={travelTypeVisibleColumns.imputation}
                                                    onChange={() => toggleTravelTypeColumn("imputation")} />
                                                <label className="custom-control-label" htmlFor="ttcol-imputation">
                                                    {readLabel("travel_type_imputation", "Displacement Imputation")}
                                                </label>
                                            </div>
                                            <div className="custom-control custom-checkbox mb-2">
                                                <input id="ttcol-rate" type="checkbox" className="custom-control-input"
                                                    checked={travelTypeVisibleColumns.rate}
                                                    onChange={() => toggleTravelTypeColumn("rate")} />
                                                <label className="custom-control-label" htmlFor="ttcol-rate">
                                                    {readLabel("travel_type_rate", "Rate")}
                                                </label>
                                            </div>
                                            <button type="button" className="btn btn-link btn-sm p-0" onClick={resetTravelTypeVisibleColumns}>
                                                {readLabel("reset", "Reset")}
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {/* Delete (N) */}
                                <button type="button"
                                    disabled={!canEdit || travelTypeDeleteSelection.length === 0}
                                    onClick={() => setShowTravelTypeDeleteModal(true)}
                                    className="btn btn-secondary btn-sm ml-2">
                                    {data.t.read("delete")} ({travelTypeDeleteSelection.length})
                                </button>
                                {/* Add */}
                                {canEdit && (
                                    <button type="button" className="btn btn-primary btn-sm ml-2"
                                        data-testid="travel-type-add"
                                        onClick={openNewTravelType}
                                        aria-label="add">
                                        {data.t.read("add")}
                                    </button>
                                )}
                            </div>
                        </div>
                        <div className="card rounded-lg admin-table" style={{ maxHeight: "35vh", overflowY: "auto" }}>
                            <table className="table table-hover font-small mb-0">
                                <thead>
                                    <tr>
                                        <th style={{ width: "40px" }}>
                                            <Checkbox
                                                disabled={!(rates.mileage_travel_types?.length)}
                                                isChecked={isAllTravelTypesSelected()}
                                                onChange={toggleAllTravelTypesSelected}
                                            />
                                        </th>
                                        <th>{readLabel("travel_type_name", "Name")}</th>
                                        {travelTypeVisibleColumns.clientScope && <th>{readLabel("travel_type_client_scope", "Client Scope")}</th>}
                                        {travelTypeVisibleColumns.billableScope && <th>{readLabel("travel_type_billable_scope", "Billable Scope")}</th>}
                                        {travelTypeVisibleColumns.travelMode && <th>{readLabel("travel_type_travel_mode", "Travel Mode")}</th>}
                                        {travelTypeVisibleColumns.imputation && <th>{readLabel("travel_type_imputation", "Displacement Imputation")}</th>}
                                        {travelTypeVisibleColumns.rate && <th>{readLabel("travel_type_rate", "Rate")}</th>}
                                        <th style={{ width: "90px" }}>{readLabel("travel_type_active", "Active")}</th>
                                        <th style={{ width: "80px" }}>{readLabel("table_actions", "Actions")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedTravelTypes.map(tt => {
                                        const linkedImp = displacementImputations.find(
                                            d => String(imputationDocumentId(d)) === String(tt.imputation_id)
                                        );
                                        const isSelected = travelTypeDeleteSelection.includes(tt.id);
                                        return (
                                            <tr key={tt.id}>
                                                <td onClick={e => e.stopPropagation()}>
                                                    <Checkbox
                                                        isChecked={isSelected}
                                                        onChange={() => {
                                                            if (isSelected) {
                                                                setTravelTypeDeleteSelection(prev => prev.filter(x => x !== tt.id));
                                                            } else {
                                                                setTravelTypeDeleteSelection(prev => [...prev, tt.id]);
                                                            }
                                                        }}
                                                    />
                                                </td>
                                                <td>{tt.name}</td>
                                                {travelTypeVisibleColumns.clientScope && (
                                                    <td>{readLabel(`travel_type_scope_${tt.client_scope}`,
                                                        tt.client_scope === "client" ? "Client" : "Internal")}</td>
                                                )}
                                                {travelTypeVisibleColumns.billableScope && (
                                                    <td>{readLabel(`travel_type_${tt.billable_scope}`,
                                                        tt.billable_scope === "billable" ? "Billable" : "Non-billable")}</td>
                                                )}
                                                {travelTypeVisibleColumns.travelMode && (
                                                    <td>{readLabel(
                                                        `travel_type_mode_${tt.travel_mode}`,
                                                        tt.travel_mode === "office_travel" ? "Office Travel" :
                                                        tt.travel_mode === "other_travel" ? "Other Travel" :
                                                        tt.travel_mode === "open_amount" ? "Open Amount" :
                                                        tt.travel_mode
                                                    )}</td>
                                                )}
                                                {travelTypeVisibleColumns.imputation && (
                                                    <td>{linkedImp ? imputationConfigRowDisplayName(linkedImp, categoryLabel, data) : tt.imputation_id}</td>
                                                )}
                                                {travelTypeVisibleColumns.rate && (
                                                    <td>{tt.travel_mode === "open_amount" ? "—" : `${tt.rate} ${tt.currency}/${tt.distance_unit}`}</td>
                                                )}
                                                <td className="text-center">
                                                    <Switch
                                                        checked={!!tt.active}
                                                        onChange={() => toggleTravelTypeActive(tt)}
                                                        width={70}
                                                        onColor="#F39800"
                                                        onHandleColor="#2693e6"
                                                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                                        disabled={!canEdit}
                                                        id={`tt-active-${tt.id}`}
                                                    />
                                                </td>
                                                <td>
                                                    <button type="button"
                                                        className="btn btn-link btn-sm p-0 mr-2"
                                                        aria-label="edit"
                                                        onClick={() => openEditTravelType(tt)}>
                                                        <FontAwesomeIcon icon={faPencilAlt} />
                                                    </button>
                                                    <FontAwesomeIcon
                                                        icon={faWindowClose}
                                                        onClick={() => {
                                                            setTravelTypeDeleteSelection([tt.id]);
                                                            setShowTravelTypeDeleteModal(true);
                                                        }}
                                                        style={{ color: "red", cursor: "pointer" }}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                </div>

                {errorMessage != null && (
                    <Modal title={data.t.read("attention")} okText={"OK"} okAction={() => setErrorMessage(null)}>
                        <p style={{whiteSpace: 'pre-line'}} className='text-danger'>{errorMessage}</p>
                    </Modal>
                )}
                {successMessage != null && (
                    <Modal title={data.t.read("attention")} okText={"OK"} okAction={() => setSuccessMessage(null)}>
                        {successMessage}
                    </Modal>
                )}
                {canEdit && showDisplacementDeleteModal && (
                    <Modal
                        title="Delete"
                        okAction={deleteDisplacementImputations}
                        cancelAction={() => { setShowDisplacementDeleteModal(false); setDisplacementDeleteSelection([]); }}
                        okText={data.t.read("delete")}
                        cancelText={data.t.read("cancel")}>
                        Delete
                    </Modal>
                )}
                {showDisplacementModal && (
                    <ImputationConfigModal
                        data={data}
                        initial={displacementModalInitial}
                        imputationCatalog="displacement"
                        approvableUsers={approvableUsers}
                        departmentOptions={departmentOptions}
                        rankOptions={rankOptions}
                        onSuccess={(msg) => {
                            setSuccessMessage(msg);
                            refreshDisplacementImputations();
                            setDisplacementPage(1);
                        }}
                        onError={(msg) => setErrorMessage(msg)}
                        onClose={() => setShowDisplacementModal(false)}
                    />
                )}
                {canEdit && showTravelTypeDeleteModal && (
                    <Modal
                        title="Delete"
                        okAction={deleteSelectedTravelTypes}
                        cancelAction={() => { setShowTravelTypeDeleteModal(false); setTravelTypeDeleteSelection([]); }}
                        okText={data.t.read("delete")}
                        cancelText={data.t.read("cancel")}>
                        Delete
                    </Modal>
                )}
                {showTravelTypeModal && (
                    <form method="post" onSubmit={handleTravelTypeSubmit}>
                        <Modal
                            title={editingTravelType
                                ? readLabel("travel_type_modal_edit", "Edit Travel Type")
                                : readLabel("travel_type_modal_add", "Add Travel Type")}
                            okIsSubmit={true}
                            cancelAction={closeTravelTypeModal}
                            okText={data.t.read("save")}
                            cancelText={data.t.read("cancel")}>
                            {/* — Identity section — */}
                            <div className="card p-3 mb-3">
                                <h6 className="mb-3">{data.t.read("travel_type_section_identity")}</h6>
                                <div className="mb-3 d-flex align-items-center">
                                    <label className="col-4 col-form-label">
                                        {readLabel("travel_type_name", "Name")}{REQUIRED_MARK}
                                    </label>
                                    <div className="col-8">
                                        <input
                                            data-testid="travel-type-name-input"
                                            className="form-control"
                                            type="text"
                                            value={travelTypeForm.name || ""}
                                            onChange={e => { const nextName = e.target.value; setTravelTypeFormErrors([]); setTravelTypeForm(prev => ({ ...prev, name: nextName })); }}
                                            disabled={!canEdit}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3 d-flex align-items-center">
                                    <label className="col-4">{readLabel("travel_type_client_scope", "Client Scope")}{REQUIRED_MARK}</label>
                                    <div className="col-8">
                                        <select className="form-control"
                                            data-testid="travel-type-client-scope-select"
                                            value={travelTypeForm.client_scope || "client"}
                                            onChange={e => {
                                                const nextClientScope = e.target.value;
                                                setTravelTypeForm(prev => ({
                                                    ...prev,
                                                    client_scope: nextClientScope,
                                                    // XPEN-2871: Internal must never be Billable — snap back immediately
                                                    // instead of leaving a stale, now-invalid value in state.
                                                    billable_scope: nextClientScope === "internal" ? "non_billable" : prev.billable_scope,
                                                }));
                                            }}
                                            disabled={!canEdit}>
                                            <option value="client">{readLabel("travel_type_scope_client", "Client")}</option>
                                            <option value="internal">{readLabel("travel_type_scope_internal", "Internal")}</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mb-3 d-flex align-items-center">
                                    <label className="col-4">{readLabel("travel_type_billable_scope", "Billable Scope")}{REQUIRED_MARK}</label>
                                    <div className="col-8">
                                        <select className="form-control"
                                            data-testid="travel-type-billable-scope-select"
                                            value={travelTypeForm.billable_scope || "non_billable"}
                                            onChange={e => {
                                                const nextBillableScope = e.target.value;
                                                setTravelTypeForm(prev => ({ ...prev, billable_scope: nextBillableScope }));
                                            }}
                                            disabled={!canEdit}>
                                            {travelTypeForm.client_scope !== "internal" && (
                                                <option value="billable">{readLabel("travel_type_billable", "Billable")}</option>
                                            )}
                                            <option value="non_billable">{readLabel("travel_type_non_billable", "Non-billable")}</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mb-0 d-flex align-items-center">
                                    <label className="col-4">{readLabel("travel_type_travel_mode", "Travel Mode")}{REQUIRED_MARK}</label>
                                    <div className="col-8">
                                        <select className="form-control"
                                            data-testid="travel-type-mode-select"
                                            value={travelTypeForm.travel_mode || "office_travel"}
                                            onChange={e => { const nextMode = e.target.value; setTravelTypeForm(prev => ({ ...prev, travel_mode: nextMode })); }}
                                            disabled={!canEdit}>
                                            <option value="office_travel">{readLabel("travel_type_mode_office_travel", "Office Travel")}</option>
                                            <option value="other_travel">{readLabel("travel_type_mode_other_travel", "Other Travel")}</option>
                                            <option value="open_amount">{readLabel("travel_type_mode_open_amount", "Open Amount")}</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* — Coding & Rate section — */}
                            <div className="card p-3 mb-3">
                                <h6 className="mb-3">{data.t.read("travel_type_section_coding_rate")}</h6>
                                <div className="mb-3 d-flex align-items-center">
                                    <label className="col-4">
                                        {data.t.read("category")}{REQUIRED_MARK}
                                    </label>
                                    <div className="col-8">
                                        <select
                                            data-testid="travel-type-imputation-select"
                                            className="form-control"
                                            value={selectedDisplacementRoot ? String(imputationDocumentId(selectedDisplacementRoot)) : ""}
                                            onChange={e => {
                                                setTravelTypeFormErrors([]);
                                                const rootId = e.target.value;
                                                setTravelTypePickerRootId(rootId);
                                                if (!rootId) {
                                                    setTravelTypeForm(prev => ({ ...prev, imputation_id: "" }));
                                                    return;
                                                }
                                                const hasChildren = displacementImputations.some(
                                                    imp => imputationRowHasParentId(imp) && String(imp.parent_id) === rootId
                                                );
                                                setTravelTypeForm(prev => ({
                                                    ...prev,
                                                    imputation_id: hasChildren ? "" : rootId,
                                                }));
                                            }}
                                            disabled={!canEdit}>
                                            <option value="">
                                                {readLabel("travel_type_select_imputation", "— Select category —")}
                                            </option>
                                            {displacementRootOptions.map(imp => {
                                                const impId = String(imputationDocumentId(imp));
                                                return (
                                                    <option key={impId} value={impId}>
                                                        {imputationConfigRowDisplayName(imp, categoryLabel, data)}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </div>
                                {displacementChildrenForPicker.length > 0 && (
                                    <div className="mb-3 d-flex align-items-center">
                                        <label className="col-4">
                                            {readLabel("field_imputation_subcategory", "Subcategory")}{REQUIRED_MARK}
                                        </label>
                                        <div className="col-8">
                                            <select
                                                data-testid="travel-type-imputation-child-select"
                                                className="form-control"
                                                value={travelTypeForm.imputation_id || ""}
                                                onChange={e => {
                                                    setTravelTypeFormErrors([]);
                                                    setTravelTypeForm(prev => ({ ...prev, imputation_id: e.target.value }));
                                                }}
                                                disabled={!canEdit}>
                                                <option value="">
                                                    {readLabel("travel_type_select_subcategory", "— Select subcategory —")}
                                                </option>
                                                {displacementChildrenForPicker.map(imp => {
                                                    const impId = String(imputationDocumentId(imp));
                                                    return (
                                                        <option key={impId} value={impId}>
                                                            {imputationConfigRowDisplayName(imp, categoryLabel, data)}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    </div>
                                )}
                                {travelTypeForm.travel_mode !== "open_amount" && (
                                    <div className="mb-3 d-flex align-items-center">
                                        <label className="col-4">
                                            {readLabel("travel_type_rate", "Rate (per km/mile)")}{REQUIRED_MARK}
                                        </label>
                                        <div className="col-8">
                                            <MoneyInput
                                                data-testid="travel-type-rate-input"
                                                value={travelTypeForm.rate === "" || travelTypeForm.rate == null ? "" : Number(travelTypeForm.rate)}
                                                onChange={(value) => { setTravelTypeFormErrors([]); setTravelTypeForm(prev => ({ ...prev, rate: value })); }}
                                                isReadOnly={!canEdit}
                                                locale={`${data.t.lang}-${data.t.lang.toUpperCase()}`}
                                                placeholder={readLabel("travel_type_rate", "Rate (per km/mile)")}
                                                className="form-control"
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="mb-3 d-flex align-items-center">
                                    <label className="col-4" htmlFor="travel-type-currency">{readLabel("travel_type_currency", "Currency")}{REQUIRED_MARK}</label>
                                    <div className="col-8">
                                        <CurrencySelect
                                            inputId="travel-type-currency"
                                            value={travelTypeForm.currency}
                                            onChange={code => setTravelTypeForm(prev => ({ ...prev, currency: code }))}
                                            disabled={!canEdit}
                                            data={data}
                                        />
                                    </div>
                                </div>
                                {travelTypeForm.travel_mode !== "open_amount" && (
                                    <div className="mb-0 d-flex align-items-center">
                                        <label className="col-4">{readLabel("travel_type_distance_unit", "Distance Unit")}{REQUIRED_MARK}</label>
                                        <div className="col-8">
                                            <select className="form-control"
                                                data-testid="travel-type-distance-unit-select"
                                                value={travelTypeForm.distance_unit || ""}
                                                onChange={e => { const nextUnit = e.target.value; setTravelTypeForm(prev => ({ ...prev, distance_unit: nextUnit })); }}
                                                disabled={!canEdit}>
                                                <option value="" disabled hidden>{readLabel("select_placeholder", "Select an option")}</option>
                                                <option value="km">km</option>
                                                <option value="miles">miles</option>
                                            </select>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* — Visibility section — */}
                            <div className="card p-3 mb-3">
                                <h6 className="mb-3">{data.t.read("travel_type_section_visibility")}</h6>
                                <div className="mb-3 d-flex align-items-start">
                                    <label className="col-4 col-form-label pt-2" htmlFor="travel-type-departments">
                                        {data.t.read("imputation_departments")}
                                    </label>
                                    <div className="col-8">
                                        {renderTravelTypeVisibilityPicker({
                                            inputId: "travel-type-departments",
                                            selectedIds: travelTypeForm.department_ids,
                                            onChangeIds: nextIds => setTravelTypeForm(prev => ({
                                                ...prev,
                                                department_ids: nextIds,
                                            })),
                                            options: departmentOptions,
                                            resolveLabel: id => departmentIdToLabel[id] || id,
                                            placeholder: data.t.read("imputation_departments_placeholder"),
                                            chipTestId: "travel-type-departments-chips",
                                        })}
                                    </div>
                                </div>
                                {hasAderantIntegration && (
                                    <div className="mb-3 d-flex align-items-start">
                                        <label className="col-4 col-form-label pt-2" htmlFor="travel-type-ranks">
                                            {data.t.read("imputation_ranks")}
                                        </label>
                                        <div className="col-8">
                                            {renderTravelTypeVisibilityPicker({
                                                inputId: "travel-type-ranks",
                                                selectedIds: travelTypeForm.rank_ids,
                                                onChangeIds: nextIds => setTravelTypeForm(prev => ({
                                                    ...prev,
                                                    rank_ids: nextIds,
                                                })),
                                                options: rankOptions,
                                                resolveLabel: id => rankIdToLabel[id] || id,
                                                placeholder: data.t.read("imputation_ranks_placeholder"),
                                                chipTestId: "travel-type-ranks-chips",
                                            })}
                                        </div>
                                    </div>
                                )}
                                <div className="mb-0 d-flex align-items-center">
                                    <label className="col-4">{readLabel("travel_type_active", "Active")}</label>
                                    <div className="col-8">
                                        <Switch
                                            checked={!!travelTypeForm.active}
                                            onChange={(active) => setTravelTypeForm(prev => ({ ...prev, active }))}
                                            width={62}
                                            height={28}
                                            onColor="#F39800"
                                            onHandleColor="#ffffff"
                                            offColor="#D1D5DB"
                                            boxShadow="0 1px 3px rgba(15, 23, 42, 0.18)"
                                            activeBoxShadow="0 0 0 2px rgba(243, 152, 0, 0.15)"
                                            disabled={!canEdit}
                                            id="travel-type-active-switch"
                                        />
                                    </div>
                                </div>
                            </div>

                            {travelTypeFormErrors.length > 0 && (
                                <div className="alert alert-danger mt-3 mb-0" role="alert"
                                    data-testid="travel-type-form-errors">
                                    <ul className="mb-0 pl-3">
                                        {travelTypeFormErrors.map((msg, i) => <li key={i}>{msg}</li>)}
                                    </ul>
                                </div>
                            )}
                        </Modal>
                    </form>
                )}
            </div>
        </div>
    </div>);
};

export default inject('data')(observer(ExpensesRates));
